<template>
	<Loading :loading="loading">
		<div v-if="application">
			<div
				v-if="$isOrgContext && canRecruitApplication && application.status == 'pending'"
				class="flex gap-1"
			>
				<DeclineApplicationAction
					:application-id="application.id"
					:applications="[application]"
					compact
					button-type="tertiary"
					button-class="border text-darkish border-darkish"
					:loading="loading"
					@update="decline"
				/>
				<Button
					type="success"
					class="ml-1 border border-success"
					:title="$t('crud.accept')"
					@click="
						$emit('updated:application', [
							application.id,
							'accepted'
						])
					"
				>
					<Icon :icon="['far', 'check']" />
				</Button>
			</div>
			<div v-else-if="applicantCanWithdraw" class="flex items-center gap-3">
				<Badge
					v-if="!showsAppliedBadge && application.status != 'invitation'"
					color="gray"
					variant="statusTag"
				>
					{{ $t(applicationStageLabelKey(application.status)) }}
				</Badge>
				<WithdrawApplicationAction
					:application="application"
					:job-offer-name="jobOffer && jobOffer.name"
					button-type="secondary"
					:loading="loading"
					@update="withdraw"
				/>
				<span
					v-if="showsAppliedBadge"
					class="inline-flex h-10 items-center gap-2 rounded-lg bg-[#E6F4EC] px-3.5 text-base font-medium text-[#1E442E]"
				>
					<IconCheckCircle :size="20" />
					{{ $t('general.requested') }}
				</span>
				<Button
					v-if="
						application.status == 'invitation' &&
							(!jobOffer || jobOffer.active !== false)
					"
					type="success"
					class="border border-success"
					:title="$t('crud.accept')"
					@click="
						$emit('updated:application', [
							application.id,
							'accepted'
						])
					"
				>
					<Icon :icon="['far', 'check']" />
				</Button>
			</div>
			<div class="text-muted" v-else>
				<Badge
					v-if="application.status == 'denied'"
					color="gray"
					variant="statusTag"
				>
					{{
						$t(
							applicationStageLabelKey(
								application.status,
								application.decline_source
							)
						)
					}}
				</Badge>
				<Button
					v-else-if="application.status == 'accepted'"
					type="tertiary"
				>
					{{ $t(applicationStageLabelKey(application.status)) }}
				</Button>
				<Badge v-else color="gray" variant="statusTag">
					{{ $t(applicationStageLabelKey(application.status)) }}
				</Badge>
			</div>
		</div>
		<Button v-else-if="canApply" @click="requestApply" type="primary">
			<div>{{ primaryCtaLabel }}</div>
		</Button>
		<Button
			v-else-if="showCompleteProfileButton"
			type="primary"
			@click="continueApplication"
			:title="$t('application.inactive-profile-cannot-apply')"
		>
			<div>
				{{ $t('application.continue-application') }}
			</div>
		</Button>

		<!-- Applications paused -->
		<Badge
			v-else-if="applicationsPaused"
			color="light"
			variant="compactRounded"
		>
			{{ $t('job-offer.not-receiving-applications') }}
		</Badge>

		<ReviewDocumentsModal
			v-if="showDocumentReview"
			:applicant-profile-id="$applicantProfile.id"
			:job-offer-name="(jobOffer || {}).name"
			:company-name="(jobOffer || {}).company"
			@close="showDocumentReview = false"
			@submit="submitReviewedApplication"
		/>
	</Loading>
</template>

<script>
import HasLoadingState from '~/mixins/hasLoadingState'
import Loading from '~/components/ui/common/Loading'
import Button from '~/components/ui/common/Button'
import Icon from '~/components/ui/common/Icon'
import Badge from '~/components/ui/common/Badge'
import { IconCheckCircle } from '~/components/Icons'
import ReviewDocumentsModal from '~/components/ui/modals/ReviewDocumentsModal'
import {
	DeclineApplicationAction,
	WithdrawApplicationAction
} from '~/components/Applications'
import { applicationIntent } from '~/utils/applicationIntent'
import { getJobOfferRedirectPath } from '~/utils/jobOfferRedirect'
import { getSafeRedirectQuery } from '~/utils/safeRedirect'
import { onboardingRedirectPath } from '~/utils/onboardingRoute'
import {
	canApplicantWithdrawApplication,
	getApplicationStageLabelKey
} from '~/utils/applicantApplicationStage'

export default {
	components: {
		Loading,
		Button,
		Icon,
		Badge,
		IconCheckCircle,
		DeclineApplicationAction,
		WithdrawApplicationAction,
		ReviewDocumentsModal
	},
	mixins: { HasLoadingState },
	props: {
		application: {
			required: false,
			type: Object
		},
		readyToSubmitApplication: {
			type: Boolean,
			required: false,
			default: false
		},
		jobOffer: {
			required: false,
			type: Object,
			default: null
		},
		// Apply source claimed by the job page for this visit; re-stamped when
		// the applicant continues into setup so the auto-apply keeps it.
		applySource: {
			required: false,
			type: String,
			default: null
		},
		loading: {
			required: false,
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			showDocumentReview: false
		}
	},
	computed: {
		canRecruitApplication() {
			return this.application?.capabilities?.recruit === true
		},
		applicantCanWithdraw() {
			return (
				!this.$isOrgContext &&
				canApplicantWithdrawApplication(this.application, this.jobOffer)
			)
		},
		showsAppliedBadge() {
			return ['pending', 'requested', 'accepted'].includes(
				this.application?.status
			)
		},
		primaryCtaLabel() {
			if (this.$org) {
				return this.$t('general.invite')
			}

			return this.readyToSubmitApplication
				? this.$t('application.submit-application')
				: this.$t('general.apply')
		},
		isApplicationsPaused() {
			return !this.$isOrgContext && !!this.jobOffer?.applications_paused
		},
		canApply() {
			if (this.$isOrgContext) {
				return this.jobOffer?.capabilities?.recruit === true
			}
			// Applicants can only apply if their profile is active
			return (
				this.$applicantProfile &&
				this.$applicantProfile.active &&
				this.jobOffer?.active !== false &&
				!this.isApplicationsPaused
			)
		},
		showCompleteProfileButton() {
			if (
				this.$isOrgContext ||
				this.application ||
				this.applicationsPaused
			) {
				return false
			}

			return !!this.$applicantProfile && !this.$applicantProfile.active
		},
		applicationsPaused() {
			return !!this.jobOffer?.applications_paused
		}
	},
	methods: {
		// Applicants confirm the documents organisations will see; org invites skip the review.
		requestApply() {
			if (this.$isOrgContext) {
				this.$emit('submitted:apply')
				return
			}
			this.showDocumentReview = true
		},
		submitReviewedApplication() {
			this.showDocumentReview = false
			this.$emit('submitted:apply')
		},
		applicationStageLabelKey(status, declineSource = null) {
			return getApplicationStageLabelKey(
				status,
				!!this.$isOrgContext,
				declineSource
			)
		},
		withdraw(id, status) {
			this.$emit('updated:application', [id, status])
		},
		decline(id, status, sendRejectionEmail, rejectionResult) {
			if (this.$isOrgContext && !this.canRecruitApplication && !rejectionResult) return
			this.$emit('updated:application', [
				id,
				status,
				sendRejectionEmail,
				...(rejectionResult ? [rejectionResult] : [])
			])
		},
		rememberApplicationIntent() {
			if (!this.jobOffer?.id) {
				return null
			}

			const redirectPath = getJobOfferRedirectPath(
				this.jobOffer,
				this.$route?.fullPath || '/',
				typeof window !== 'undefined' && window.location
					? window.location.origin
					: undefined
			)

			applicationIntent.setRedirectToPath(redirectPath)
			applicationIntent.setJobOfferId(this.jobOffer.id)
			if (this.applySource) {
				applicationIntent.setApplySource(this.jobOffer.id, this.applySource)
			}

			return redirectPath
		},
		async continueApplication() {
			const id = this.$applicantProfile && this.$applicantProfile.id
			if (id) {
				const redirectPath = this.rememberApplicationIntent()
				const applicantProfile =
					(await this.$store.dispatch(
						'applicantProfile/needApplicantProfile',
						true
					)) || this.$applicantProfile

				const onboardingPath = onboardingRedirectPath(applicantProfile)

				if (!onboardingPath) {
					this.requestApply()
					return
				}

				this.$router.push({
					path: onboardingPath,
					query: getSafeRedirectQuery(redirectPath)
				})
			} else {
				// Fallback: if for any reason profile is missing, send user to login/register
				this.$router.push('/applicant-profile')
			}
		}
	}
}
</script>
